import { useEffect, useRef, useState } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface ConfirmButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  onConfirm: () => void
  /** Label shown while armed (defaults to «Delete?»). */
  confirmLabel?: ReactNode
  /**
   * icon (default) — for trash icons in rows: grey icon that arms into a small danger pill.
   * button — full-size danger button styled like the primary button (for panel footers).
   */
  variant?: 'icon' | 'button'
}

/**
 * Inline two-step destructive button: the first click arms it («Delete?»), a second click within
 * {@link ARM_WINDOW_MS} confirms; after that it disarms on its own. No window.confirm.
 *
 * The window used to be 3 s and the button also disarmed on blur — which made the confirmation
 * vanish while the user was still reaching for it ("if you're not quick enough, it's gone"), and
 * inside popovers a stray focus change cancelled it outright.
 */
/** How long the armed «Delete?» state stays clickable. */
const ARM_WINDOW_MS = 8000

export default function ConfirmButton({
  onConfirm,
  confirmLabel = 'Delete?',
  variant = 'icon',
  className,
  children,
  ...props
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  function disarm() {
    window.clearTimeout(timer.current)
    setArmed(false)
  }

  function handleClick() {
    if (!armed) {
      setArmed(true)
      timer.current = window.setTimeout(() => setArmed(false), ARM_WINDOW_MS)
      return
    }
    disarm()
    onConfirm()
  }

  const cls =
    variant === 'button'
      ? cn(
          'px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60',
          armed
            ? 'bg-danger text-white hover:opacity-90'
            : 'bg-white border border-danger/40 text-danger hover:bg-danger/5',
        )
      : armed
        ? 'text-[11px] font-medium bg-danger text-white rounded px-1.5 py-0.5'
        : 'text-gray-400 hover:text-danger disabled:opacity-50'

  return (
    <button type="button" onClick={handleClick} className={cn(cls, className)} {...props}>
      {armed ? confirmLabel : children}
    </button>
  )
}
